/**
 * BrazeSelect with AI agent tool registration.
 */
import React, { useCallback, useMemo, useEffect, forwardRef, useRef } from "react";

import { Icon, useSummaryContext } from "@braze/beacon-core";
import type { DefaultOptionType, GroupBase, SelectProps } from "@braze/beacon-lab-select";
import { Select } from "@braze/beacon-lab-select";

import {
  selectOptionDescription,
  selectOptionParameters,
  selectOptionsDescription,
  selectOptionsParameters,
} from "@lib/agenticAi/sharedToolContracts";

import useI18nNamespace from "@services/useI18nNamespace";
import { useOperatorEntityId } from "@services/useOperatorEntityId";
import { useRegisterChangeTool } from "@services/useRegisterChangeTool";

import { DiffDisplay } from "@components/AgenticAi/components/DiffDisplay";

import { selectToolIconName } from "./constants";
import { useCachedOptions, useClearTool, useSearchTool } from "./hooks";
import type { BrazeSelectComponent, BrazeSelectProps, OnChangeHandler } from "./types";
import {
  defaultFormatOptionStringLabel,
  isSelectOptionDisabled,
  makeOptionsMap,
  resolveOptionForSelect,
  serializeSelect,
} from "./utils";

export const BrazeSelect = forwardRef(
  <OptionType = DefaultOptionType, IsMulti extends boolean = false>(
    {
      operatorLabel,
      operatorDisabledReason,
      operatorRequiresApproval = "yes",
      isDisabled,
      formatOptionStringLabel: formatOptionStringLabelProp,
      invisible = false,
      onChange,
      onOperatorSelect,
      operatorSelectCompleteLabel,
      isMulti,
      onOperatorSearch,
      operatorToolId,
      excludeOptionsFromDescription = false,
      ...props
    }: BrazeSelectProps<OptionType, IsMulti, GroupBase<OptionType>>,
    ref: React.ForwardedRef<SelectProps<OptionType, IsMulti, GroupBase<OptionType>>>
  ) => {
    const formatOptionStringLabel = formatOptionStringLabelProp ?? defaultFormatOptionStringLabel;
    const TypedSelect = Select as React.ForwardRefExoticComponent<
      SelectProps<OptionType, IsMulti, GroupBase<OptionType>> & React.RefAttributes<unknown>
    >;

    const { t } = useI18nNamespace("braze-select");
    const registerChangeTool = useRegisterChangeTool({ operatorDisabledReason });
    const fallbackId = useOperatorEntityId("braze-select");
    const id = operatorToolId ?? fallbackId;
    const { isSummary: isSummaryContext } = useSummaryContext();
    const isInSummaryMode = Boolean(props.isSummary) || isSummaryContext;

    const optionsMap = useMemo(() => {
      return makeOptionsMap(props.options || [], formatOptionStringLabel);
    }, [props.options, formatOptionStringLabel]);

    const onChangeRef = useRef(onChange as OnChangeHandler<OptionType> | undefined);
    onChangeRef.current = onChange as OnChangeHandler<OptionType> | undefined;
    const onOperatorSelectRef = useRef(onOperatorSelect);
    onOperatorSelectRef.current = onOperatorSelect;
    const optionsMapRef = useRef(optionsMap);
    optionsMapRef.current = optionsMap;
    const formatOptionStringLabelRef = useRef(formatOptionStringLabel);
    formatOptionStringLabelRef.current = formatOptionStringLabel;
    const valueRef = useRef(props.value);
    valueRef.current = props.value;
    const onBlurRef = useRef(props.onBlur);
    onBlurRef.current = props.onBlur;

    const isToolDisabled = Boolean(isDisabled || props.readOnly || isInSummaryMode);
    const hasSelectableOption = Object.values(optionsMap).some((option) => !isSelectOptionDisabled(option));

    const { cachedOptionsRef, updateCachedOptions } = useCachedOptions<OptionType>(formatOptionStringLabelRef);

    // When excludeOptionsFromDescription is true and no async search handler was provided,
    // synthesize a default searchFn over the local optionsMap so Operator can still discover
    // values via `lookup_options`. The list of <option> children was removed from
    // `data-operator-description` to keep Operator context small, so this is the documented
    // discovery path.
    const defaultLocalSearchFn = useCallback((searchQuery: string): Promise<OptionType[]> => {
      const allOptions = Object.values(optionsMapRef.current).filter((option) => !isSelectOptionDisabled(option));
      const trimmed = searchQuery.trim().toLowerCase();
      if (!trimmed) {
        return Promise.resolve(allOptions);
      }
      return Promise.resolve(
        allOptions.filter((option) => formatOptionStringLabelRef.current(option).toLowerCase().includes(trimmed))
      );
    }, []);

    const searchFn = onOperatorSearch ?? (excludeOptionsFromDescription ? defaultLocalSearchFn : undefined);
    // Force search-first only when an explicit onOperatorSearch is provided; the
    // excludeOptionsFromDescription path still searches the local options prop, so falling
    // back to optionsMapRef remains safe.
    const hasBackendSearch = Boolean(onOperatorSearch);

    useSearchTool<OptionType>({
      id,
      isDisabled: isToolDisabled,
      operatorDisabledReason,
      searchFn,
      updateCachedOptions,
      formatOptionStringLabel,
    });

    useClearTool<OptionType>({
      id,
      isDisabled: isToolDisabled,
      operatorDisabledReason,
      operatorRequiresApproval,
      isClearable: !props.readOnly && !isInSummaryMode && (props.isClearable ?? true),
      isMulti: Boolean(isMulti),
      operatorLabel,
      valueRef,
      onChangeRef,
      formatOptionStringLabel,
    });

    useEffect(() => {
      if (isToolDisabled) {
        return;
      }
      // Async selects discover options later via lookup_options. Static selects with nothing
      // selectable should not register a tool whose only targets cannot be chosen.
      if (!hasBackendSearch && !hasSelectableOption) {
        return;
      }

      if (isMulti) {
        const cleanup = registerChangeTool({
          toolEntityId: id,
          name: "select_options",
          description: selectOptionsDescription,
          parameters: selectOptionsParameters,
          callback: ({ optionValues }) => {
            const selectedOptions = optionValues.map((optionValue) =>
              resolveOptionForSelect<OptionType>({
                optionValue,
                optionsMap: optionsMapRef.current,
                cachedOptions: cachedOptionsRef.current,
                hasSearchFn: hasBackendSearch,
              })
            ) as OptionType[];

            const actionMeta = {
              action: "select-option",
              option: selectedOptions[selectedOptions.length - 1],
            } as const;
            const operatorSelect = onOperatorSelectRef.current;
            const selectionResult = operatorSelect ? operatorSelect(selectedOptions, actionMeta) : undefined;
            if (!operatorSelect) {
              onChangeRef.current?.(selectedOptions, actionMeta);
            }
            // Blur after changing, as BrazeInput does, so consumers that only act on blur react to
            // an agent's change: DataTable multi-select filters batch their picks and write
            // `columnFilters` on blur. Keep this synchronous: DataTableSelectFilter stores the full
            // pending selection in a ref, while its reducer processes only the final action option.
            // Deferring blur allows that reducer render to overwrite the complete pending value.
            onBlurRef.current?.({ target: { name: props.name } } as React.FocusEvent<HTMLInputElement>);
            return selectionResult ?? `Selected ${selectedOptions.length} option(s).`;
          },
          options: {
            requiredSkill: "page-controls",
            toolGroup: "Agentic Creation & Editing",
            labels: {
              ready: t("select-options-ready", "Preparing to select options"),
              running: t("select-options-running", "Selecting options"),
              complete: operatorSelectCompleteLabel ?? t("select-options-complete", "Selected options"),
              failed: t("select-options-failed", "Failed to select options"),
              stopped: t("select-options-stopped", "Stopped selecting options"),
            },
            requiresApproval: operatorRequiresApproval,
            argumentsSummaryText: ({ optionValues }) =>
              optionValues
                .map((v) => {
                  const option = optionsMapRef.current[v];
                  return option ? formatOptionStringLabelRef.current(option) : v;
                })
                .join(", "),
            renderApproval: ({ args: { optionValues } }) => {
              const newLabels = optionValues
                .map((v) => {
                  const option = optionsMapRef.current[v];
                  return option ? formatOptionStringLabelRef.current(option) : v;
                })
                .join(", ");
              const currentLabels = Array.isArray(valueRef.current)
                ? (valueRef.current as OptionType[]).map((v) => formatOptionStringLabelRef.current(v)).join(", ")
                : "";
              return <DiffDisplay title={operatorLabel} original={currentLabels} modified={newLabels} />;
            },
            toolIcon: <Icon name={selectToolIconName} />,
          },
        });
        return cleanup;
      }

      const cleanup = registerChangeTool({
        toolEntityId: id,
        name: "select_option",
        description: selectOptionDescription,
        parameters: selectOptionParameters,
        callback: ({ optionValue }) => {
          const option = resolveOptionForSelect<OptionType>({
            optionValue,
            optionsMap: optionsMapRef.current,
            cachedOptions: cachedOptionsRef.current,
            hasSearchFn: hasBackendSearch,
          });

          const actionMeta = {
            action: "select-option" as const,
            name: props.name,
            option,
          };
          const operatorSelect = onOperatorSelectRef.current;
          const selectionResult = operatorSelect ? operatorSelect(option, actionMeta) : undefined;
          if (!operatorSelect) {
            onChangeRef.current?.(option, actionMeta);
          }
          // See the multi-select branch above: blur so blur-only consumers react to the change.
          window.setTimeout(() => {
            onBlurRef.current?.({ target: { name: props.name } } as React.FocusEvent<HTMLInputElement>);
          }, 0);
          return selectionResult ?? "Option was successfully selected.";
        },
        options: {
          requiredSkill: "page-controls",
          toolGroup: "Agentic Creation & Editing",
          labels: {
            ready: t("select-option-ready", "Preparing to select"),
            running: t("select-option-running", "Selecting"),
            complete: operatorSelectCompleteLabel ?? t("select-option-complete", "Selected"),
            failed: t("select-option-failed", "Failed to select"),
            stopped: t("select-option-stopped", "Stopped selecting"),
          },
          requiresApproval: operatorRequiresApproval,
          argumentsSummaryText: ({ optionValue }) => {
            const option = optionsMapRef.current[optionValue];
            return option ? formatOptionStringLabelRef.current(option) : optionValue;
          },
          renderApproval: ({ args: { optionValue } }) => {
            const newOption = optionsMapRef.current[optionValue];
            const newLabel = newOption ? formatOptionStringLabelRef.current(newOption) : optionValue;
            const currentLabel = valueRef.current
              ? formatOptionStringLabelRef.current(valueRef.current as OptionType)
              : "";
            return <DiffDisplay title={operatorLabel} original={currentLabel} modified={newLabel} />;
          },
          toolIcon: <Icon name={selectToolIconName} />,
        },
      });
      return cleanup;
    }, [
      isToolDisabled,
      hasSelectableOption,
      registerChangeTool,
      operatorRequiresApproval,
      id,
      t,
      isMulti,
      operatorLabel,
      operatorSelectCompleteLabel,
      cachedOptionsRef,
      hasBackendSearch,
      props.name,
    ]);

    const operatorDescription = useMemo(() => {
      let selectedValues: OptionType[] = [];
      if (props.value) {
        selectedValues = Array.isArray(props.value) ? (props.value as OptionType[]) : [props.value as OptionType];
      }

      const select = serializeSelect(
        id,
        operatorLabel,
        optionsMap,
        formatOptionStringLabelRef.current,
        isDisabled || false,
        isMulti || false,
        selectedValues,
        excludeOptionsFromDescription
      );

      return select.outerHTML;
    }, [id, operatorLabel, optionsMap, isDisabled, props.value, isMulti, excludeOptionsFromDescription]);

    return (
      <div style={{ display: "contents" }} data-operator-description={operatorDescription}>
        {!invisible && (
          <TypedSelect
            aria-label={operatorLabel}
            ref={ref}
            {...props}
            onChange={onChange}
            isDisabled={isDisabled}
            isMulti={isMulti}
          />
        )}
      </div>
    );
  }
) as BrazeSelectComponent;
